// Row contracts: the API form of each table a response exposes. Keys are the SQL column names,
// nullability is the column's, enums are the CHECK's, `timestamptz` is an ISO string, `numeric`
// is a decimal string, `bigint` money is a JSON integer, and secrets are omitted.
// `contract-drift.test.ts` compares every field here with its Drizzle column, so a column change
// without a contract change (or the reverse) fails the build. The jsonb shapes defined here are
// also the `$type`s of the Drizzle columns (the tables import them), so they cannot drift either.
import { z } from "zod";
import { TIME_ENTRY_FAMILY } from "../de-metric-catalog";
import { ActorShortColumns } from "./actor";
import {
  AddonKey,
  AgentActionClass,
  AgentKeyKind,
  AgentKeyScope,
  AgentKeyStatus,
  AgentRequestKind,
  AgentRequestState,
  AgentUseSource,
  ApprovalStatus,
  ChatSurface,
  CompanyAgentMode,
  CompanyKind,
  ConsumerState,
  CostEventKind,
  Currency,
  DePauseReason,
  DigitalEmployeeStatus,
  EntitlementSource,
  EntitlementStatus,
  EventMeasure,
  ExternalIdentityKind,
  FieldType,
  FreezeOutboxMode,
  FridayReportState,
  GapPolicy,
  GoalKind,
  GoalState,
  GoalTrack,
  HelperRequestStage,
  InboundDeliveryStatus,
  InboundDeliveryVerification,
  InboundWebhookScreenMode,
  InboundWebhookStatus,
  InboundWebhookVerification,
  IngestKeySource,
  InvitationRole,
  InvitationStatus,
  KpiGroup,
  KpiPrincipalKind,
  KpiScope,
  MaintenanceTier,
  MembershipKind,
  MembershipStatus,
  MeterSettlementResolution,
  MetricAggregation,
  MetricCadence,
  MetricDirection,
  MetricNormalization,
  MetricSource,
  MetricUnit,
  ModelAccessMode,
  NotificationPreset,
  ObservationSource,
  ObservationStatus,
  PeriodType,
  RecurringWorkStatus,
  ResponsibilityStatus,
  RunEffortReasoning,
  RunEffortRequest,
  RuntimeRefSource,
  SeatAssignmentCapacity,
  SeatDeFill,
  SeatEdgeKind,
  SeatHolderKind,
  SeatKind,
  SeatOrigin,
  SeatStatus,
  SkillGuardMode,
  SubjectKind,
  SubscriptionCadence,
  TeamComposition,
  TeamRollup,
  TimeEntryGrain,
  TimeEntryKind,
  TimeEntrySource,
  TrackingMode,
  VerificationState,
  ViewKind,
  Visibility,
  WebhookDeliveryStatus,
  WebhookStatus,
  WorkforceAgentReportsToKind,
  WorkforceAgentStatus,
} from "./enums";
import { ContextSource, FridayReport, ReportRecipient } from "./friday-report";
import { CostMeter } from "./ingest";
import {
  boundedRecord,
  Decimal,
  DottedName,
  Email,
  IsoDate,
  LIMITS,
  MicrosAmount,
  MinorAmount,
  MonthKey,
  PeriodKey,
  Slug,
  Timestamp,
  TimeZone,
  text,
  Uuid,
  WallTime,
  WeekKey,
} from "./primitives";
import {
  ClientScorecard,
  MetricBands,
  MetricGate,
  ObservationProvenance,
  Scorecard,
} from "./scorecard";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

// jsonb shapes ----------------------------------------------------------------------------------

/** A gateway model alias as LiteLLM names it (`model_name`), e.g. `claude-sonnet`, `gpt-4o`. */
export const ModelAlias = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:/@-]*$/, "a gateway model alias");
/** A residency region code (lowercase, e.g. `eu`, `us`, `uk`): where a model is served. */
export const ModelRegion = z
  .string()
  .min(2)
  .max(8)
  .regex(/^[a-z]{2,8}$/, "a lowercase region code such as eu or us");
/** A task kind: a pipeline role of the role-to-model map (`lead`, `draft`, `review`, …). */
export const ModelTaskKind = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, "a lowercase task kind such as draft or review");
/** The task kinds the Setup › Model policy page offers a default for (01 "Role-to-model map"). */
export const MODEL_TASK_KINDS = ["lead", "draft", "review", "verify"] as const;
/** `company.settings.model_policy` (M3-07; MODEL-POLICY): see `CompanySettings`. */
export const CompanyModelPolicy = z
  .strictObject({
    residency: ModelRegion.optional(),
    default_model: ModelAlias.optional(),
    allowed_models: z.array(ModelAlias).min(1).max(50).optional(),
    role_defaults: z
      .record(ModelTaskKind, ModelAlias)
      .refine((r) => Object.keys(r).length <= 20, "at most 20 task kinds")
      .meta({ maxProperties: 20 })
      .optional(),
  })
  .meta({ id: "CompanyModelPolicy" });
export type CompanyModelPolicy = z.infer<typeof CompanyModelPolicy>;

/** MODEL-KEYS-PLANS: `company.settings.model_access` (see `CompanySettings`). */
export const CompanyModelAccess = z
  .strictObject({
    mode: ModelAccessMode,
    fallback_to_ours: z.boolean(),
  })
  .meta({ id: "CompanyModelAccess" });
export type CompanyModelAccess = z.infer<typeof CompanyModelAccess>;

/**
 * The org chart's seat dimensions (CHART-DIMENSIONS, docs/design/2026-10-21-chart-dimensions.md):
 * what a seat card can show beyond its holder and title. Each is an existing CompanyOS entity:
 * `team` (team_member), `responsibilities` (responsibility rows), `kpi` (the seat's scorecard
 * band for the week, `kpi`), `load` (the seat's open Work tasks), `cost` (the seat's month from
 * budget vs actual, M5-03). `team` and `responsibilities` are the floor (every chart viewer; a
 * viewer may hide them for a cleaner view, never a permission); `kpi`, `load` and `cost` are
 * gated per company by `ChartVisibilityLevel`.
 */
export const ChartDimension = z
  .enum(["team", "responsibilities", "kpi", "load", "cost"])
  .meta({ id: "ChartDimension" });
export type ChartDimension = z.infer<typeof ChartDimension>;

/**
 * The gated dimensions: the ones an admin chooses an audience for (the floor has none).
 * `overall` (CHART-LENSES) is the Overall lens: the weighted combination of the scored lenses,
 * a summary of summaries with an audience of its own.
 */
export const ChartGatedDimension = z
  .enum(["kpi", "load", "cost", "overall", "keystones"])
  .meta({ id: "ChartGatedDimension" });
export type ChartGatedDimension = z.infer<typeof ChartGatedDimension>;

/**
 * The chart's lenses (CHART-LENSES, docs/design/2026-10-21-chart-lenses.md; KPI Beast's top
 * lens tabs, in our model): the tabs across the top of the org chart. `structure` is the floor
 * (holder, title, reporting line, teams, responsibilities); `responsibilities` scores coverage
 * (responsibilities with measurables); `performance` is the seat scorecard's band (`kpi`);
 * `execution` (CHART-KB) the scorecard's goal score, the seat's own target attainment; `tasks`
 * the open and overdue ratio (`load`); `finance` budget utilisation (`cost`); `keystones`
 * (CHART-KB) the share of the quarter's priorities on track; `completeness` (CHART-KB) how much
 * of the seat's required setup is in place; `overall` the weighted combination of the scored
 * lenses.
 */
export const ChartLens = z
  .enum([
    "structure",
    "responsibilities",
    "performance",
    "execution",
    "tasks",
    "finance",
    "keystones",
    "completeness",
    "overall",
  ])
  .meta({ id: "ChartLens" });
export type ChartLens = z.infer<typeof ChartLens>;

/** A lens band: the three colours of a lens score, or `none` when there is nothing to score. */
export const ChartLensBand = z
  .enum(["green", "amber", "red", "none"])
  .meta({ id: "ChartLensBand" });
export type ChartLensBand = z.infer<typeof ChartLensBand>;

const LensStep = z.number().min(0).max(300);
/** Two steps down: `green` at or above the first, `amber` at or above the second, red below. */
const TwoSteps = z.strictObject({ green: LensStep, amber: LensStep });

/**
 * The band steps of each scored lens, in the lens's own unit: responsibilities and overall are
 * 0–100 scores, tasks the on-time share (100 − overdue %), finance the budget used in % (amber
 * from `near`, red above `over`). Performance keeps the scorecard's own thresholds (KPI policy).
 */
export const ChartLensThresholds = z
  .strictObject({
    responsibilities: TwoSteps.optional(),
    /** CHART-KB: the goal score (target attainment, 0–100). */
    execution: TwoSteps.optional(),
    tasks: TwoSteps.optional(),
    finance: z.strictObject({ near: LensStep, over: LensStep }).optional(),
    /** CHART-KB: the share of the quarter's priorities on track, in %. */
    keystones: TwoSteps.optional(),
    overall: TwoSteps.optional(),
  })
  .meta({ id: "ChartLensThresholds" });
export type ChartLensThresholds = z.infer<typeof ChartLensThresholds>;

/** The weight of each scored lens in Overall (0–10; 0 leaves it out, like KPI Beast's dimWeights). */
export const ChartLensWeights = z
  .strictObject({
    responsibilities: z.number().min(0).max(10).optional(),
    performance: z.number().min(0).max(10).optional(),
    execution: z.number().min(0).max(10).optional(),
    tasks: z.number().min(0).max(10).optional(),
    finance: z.number().min(0).max(10).optional(),
    keystones: z.number().min(0).max(10).optional(),
  })
  .meta({ id: "ChartLensWeights" });
export type ChartLensWeights = z.infer<typeof ChartLensWeights>;

/**
 * CHART-KB: the items a seat needs before it counts as set up (the founder: "if a seat does not
 * have financials or the keystones it is not considered complete"). `holder`: someone on it,
 * or an open seat with its hiring status set; `reports_to`: a supervisor (the top of the chart
 * is exempt); `team`: a team membership; `responsibility`: at least one active responsibility;
 * `measurable`: at least one scorecard metric with a target; `finance`: a budget or a cost line
 * this month; `keystone`: at least one priority for the current quarter.
 */
export const ChartCompletenessItem = z
  .enum(["holder", "reports_to", "team", "responsibility", "measurable", "finance", "keystone"])
  .meta({ id: "ChartCompletenessItem" });
export type ChartCompletenessItem = z.infer<typeof ChartCompletenessItem>;

/** CHART-KB: which items a company requires (`settings.chart.completeness`); missing keys default to required. */
export const ChartCompletenessSettings = z
  .strictObject({
    // An object of the seven optional keys, not a record: bounded by construction (finding #6).
    required: z
      .strictObject({
        holder: z.boolean().optional(),
        reports_to: z.boolean().optional(),
        team: z.boolean().optional(),
        responsibility: z.boolean().optional(),
        measurable: z.boolean().optional(),
        finance: z.boolean().optional(),
        keystone: z.boolean().optional(),
      })
      .optional(),
    /** A seat held by a digital employee needs no budget or cost line (default true). */
    de_exempt_finance: z.boolean().optional(),
  })
  .meta({ id: "ChartCompletenessSettings" });
export type ChartCompletenessSettings = z.infer<typeof ChartCompletenessSettings>;

/**
 * Who sees a gated dimension on a seat (05 `org_chart#viewer`, narrowed per dimension):
 * `everyone` — every org-chart viewer; `reporting_line` — the seat's holders, the holders of
 * the seats above it (its supervisor chain) and company managers; `holder` — the seat's holders
 * and company managers; `managers` — company managers only (owner, admin, agency operator).
 */
export const ChartVisibilityLevel = z
  .enum(["everyone", "reporting_line", "holder", "managers"])
  .meta({ id: "ChartVisibilityLevel" });
export type ChartVisibilityLevel = z.infer<typeof ChartVisibilityLevel>;

/** The company's chart settings (`settings.chart`): the default dimensions and who sees what. */
export const ChartSettings = z
  .strictObject({
    /** The dimensions a viewer gets before choosing their own (the admin's company default). */
    dimensions: z.array(ChartDimension).max(5).optional(),
    /** The audience of each gated dimension; a missing key takes the default of the design note. */
    visibility: z
      .strictObject({
        kpi: ChartVisibilityLevel.optional(),
        load: ChartVisibilityLevel.optional(),
        cost: ChartVisibilityLevel.optional(),
        /** CHART-LENSES: the Overall lens (default `reporting_line`). */
        overall: ChartVisibilityLevel.optional(),
        /** CHART-KB: the Keystones lens (default `everyone`: 05 `goal` viewer = company#member). */
        keystones: ChartVisibilityLevel.optional(),
      })
      .optional(),
    /** CHART-KB: which setup items a seat needs to count as complete. */
    completeness: ChartCompletenessSettings.optional(),
    /** CHART-LENSES: the lens a viewer opens on before choosing one (default `structure`). */
    lens: ChartLens.optional(),
    /** CHART-LENSES: the band steps and the Overall weights (missing keys take the defaults). */
    lenses: z
      .strictObject({
        thresholds: ChartLensThresholds.optional(),
        weights: ChartLensWeights.optional(),
      })
      .optional(),
  })
  .meta({ id: "ChartSettings" });
export type ChartSettings = z.infer<typeof ChartSettings>;

/**
 * WAVE12-FIX: `settings.flags[E2E_FIXTURE_FLAG] = true` marks a company a test made or needs (the
 * e2e suites' "E2E …" companies, the seed's kpi-off gating company). The switcher leaves it out
 * for whoever reaches it only through an agency grant; its members and the operator console
 * still see it.
 */
export const E2E_FIXTURE_FLAG = "e2e_fixture";

/**
 * PLATFORM-OPERATOR: `settings.flags[DEMO_COMPANY_FLAG] = true` marks a company that holds demo
 * content only (the dev seed's demo companies; a preview stack's operator company when seeded
 * with `SEED_MARK_DEMO=1`). The demo content seeder (tools/seed-demo) writes into flagged
 * companies only and refuses any other, so it can never fill a real company with sample data.
 */
export const DEMO_COMPANY_FLAG = "demo";

/** AGENT-SAFETY: external-content screening modes (QM's `securityScreen`). */
export const ContentScreenMode = z
  .enum(["off", "observe", "enforce"])
  .meta({ id: "ContentScreenMode" });
export type ContentScreenMode = z.infer<typeof ContentScreenMode>;

/**
 * FOUNDER-OS (portfolio design 2026-10-27 §2.3): what this company's Founder OS projections carry.
 * Honoured at push time by the company's own consumer (`pushProjection`): a kind turned off is
 * pushed as `{withheld: true}`. `kpi`: `metrics` (scores plus the client view's metric values
 * and targets), `scores` (scores and bands only) or `off`; `finance`: `totals` or `off`; the
 * others on or off. Absent = the defaults by the company's relation to the portfolio holding it
 * (`owned`: everything; `client`: scores, goals and escalations only). Agency metrics, founder
 * minutes, pages, tasks, chat and memory never cross whatever the setting.
 */
export const FounderSharing = z
  .strictObject({
    kpi: z.enum(["metrics", "scores", "off"]),
    finance: z.enum(["totals", "off"]),
    goals: z.boolean(),
    issues: z.boolean(),
    escalation: z.boolean(),
    decisions: z.boolean(),
    headcount: z.boolean(),
    meetings: z.boolean(),
  })
  .meta({ id: "FounderSharing" });
export type FounderSharing = z.infer<typeof FounderSharing>;

const ClockTime = z
  .string()
  .max(5)
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");

/**
 * COS-SLICE1: quiet hours in the company's zone (`from` → `to`, wrapping midnight when `to` is
 * earlier), and whether Saturday and Sunday are quiet all day.
 */
export const CosQuietHours = z
  .strictObject({ from: ClockTime, to: ClockTime, weekends: z.boolean() })
  .meta({ id: "CosQuietHours" });
export type CosQuietHours = z.infer<typeof CosQuietHours>;

/**
 * COS-MODES (docs/design/2026-10-30-chief-of-staff-model.md §6): the Chief of Staff's mode and
 * the department level default. `mode` (default `solo`); `department_level_default` (default
 * `inform`, "Tell me") and `locked` (the default binds every head); `ceo_membership_id`, the person
 * the Chief of Staff works for (default: the person on the chart's top seat, else the first
 * owner); `suggested_heads`, the heads the CEO was already asked about (once per new head).
 */
export const COS_MODE_SETTINGS = {
  mode: z.enum(["solo", "team"]).optional(),
  department_level_default: z.enum(["hands_off", "inform", "ask"]).optional(),
  locked: z.boolean().optional(),
  ceo_membership_id: Uuid.optional(),
  suggested_heads: z.array(Uuid).max(5000).optional(),
  /** Team with no head left: Solo was suggested once (cleared when a head appears again). */
  solo_suggested: z.boolean().optional(),
} as const;

/** COS-SLICE1: the Chief of Staff loop's fields (company setting, or a DE's override). */
export const CosLoopSettings = z.strictObject({
  interval_min: z.int().min(5).max(60).optional(),
  max_runs_per_hour: z.int().min(1).max(60).optional(),
  max_runs_per_day: z.int().min(1).max(500).optional(),
  quiet_hours: CosQuietHours.nullable().optional(),
});

/** DE-BUDGET-PACE: the pace alert's thresholds and the reserve (company default or per DE). */
export const BudgetPaceSettings = z
  .strictObject({
    alert_at: z.number().min(1).max(5).optional(),
    min_used: z.number().min(0).max(1).optional(),
    second_at: z.number().min(1).max(10).optional(),
    reserve: z.number().min(0).max(0.5).optional(),
  })
  .meta({ id: "BudgetPaceSettings" });
export type BudgetPaceSettings = z.infer<typeof BudgetPaceSettings>;

/** The company's pace defaults and its per-DE overrides (`settings.budget_pace`). */
export const CompanyBudgetPace = BudgetPaceSettings.extend({
  // Bounded (request bounds, finding #6): one entry per DE, at most 500.
  for_de: boundedRecord(BudgetPaceSettings, 500, Uuid).optional(),
}).meta({ id: "CompanyBudgetPace" });
export type CompanyBudgetPace = z.infer<typeof CompanyBudgetPace>;

/**
 * RUN-EFFORT: one tier's model and reasoning. `reasoning` is what the run asks its model for
 * (`off`: no reasoning; else the reasoning effort the gateway passes on).
 */
export const RunEffortTierSpec = z
  .strictObject({ model: ModelAlias, reasoning: RunEffortReasoning })
  .meta({ id: "RunEffortTierSpec" });
export type RunEffortTierSpec = z.infer<typeof RunEffortTierSpec>;

/** RUN-EFFORT: the tier → model map (a tier left out keeps the default). */
export const RunEffortTiers = z
  .strictObject({
    low: RunEffortTierSpec.optional(),
    medium: RunEffortTierSpec.optional(),
    high: RunEffortTierSpec.optional(),
    vision: RunEffortTierSpec.optional(),
  })
  .meta({ id: "RunEffortTiers" });
export type RunEffortTiers = z.infer<typeof RunEffortTiers>;

/** RUN-EFFORT: one DE's or helper's own default effort and tier models. */
export const RunEffortAgentOverride = z
  .strictObject({ default: RunEffortRequest.optional(), tiers: RunEffortTiers.optional() })
  .meta({ id: "RunEffortAgentOverride" });
export type RunEffortAgentOverride = z.infer<typeof RunEffortAgentOverride>;

/**
 * RUN-EFFORT (`settings.run_effort`, admin-only): the tier → model map runs use, whether the
 * decide classifier may refine an automatic pick, and per-agent overrides keyed by a DE's
 * membership id or a helper's id.
 */
export const RunEffortSettings = z
  .strictObject({
    tiers: RunEffortTiers.optional(),
    refine_with_classifier: z.boolean().optional(),
    for_agent: boundedRecord(RunEffortAgentOverride, 500, Uuid).optional(),
  })
  .meta({ id: "RunEffortSettings" });
export type RunEffortSettings = z.infer<typeof RunEffortSettings>;

/**
 * B5-DECIDE (universal-task plan §b.6): the company's classifier switches, in
 * `settings.decide`. Every one is off when absent.
 * - `external`: send item text (masked: e-mail addresses and phone numbers) to Jev on
 *   OpenRouter's Decisions API with the company's OpenRouter key. Off: the company's low-tier
 *   model classifies through the company's gateway (RUN-EFFORT; masked unless the operator pinned
 *   an in-house model with `DECIDE_LOCAL_MODEL`).
 * - `board_gate`: let the classifier re-class a DE-owned board's `now` comments and `batch` edits
 *   (an FYI comment waits for the batch; an urgent edit wakes now). The deterministic classing
 *   stays the floor: an unsure or failed call keeps it.
 */
export const DecideSettingsStored = z
  .strictObject({
    external: z.boolean().optional(),
    board_gate: z.boolean().optional(),
  })
  .meta({ id: "DecideSettingsStored" });
export type DecideSettingsStored = z.infer<typeof DecideSettingsStored>;

/**
 * CORE-AUTONOMY (core plan item 6; a pending founder decision, shipped as the plan's proposal):
 * `settings.autonomy`. `thresholds` (each absent: the shipped value) gate the weekly promotion
 * check; `levels` replace a shipped level's bundle (the seat rows the level renders: a class
 * absent from every bundle is left alone). A level with `defined: false` is never promoted to.
 */
export const AutonomyLevelBundle = z
  .strictObject({
    level: z.int().min(0).max(3),
    name: z.string().min(1).max(60).optional(),
    summary: z.string().max(300).optional(),
    defined: z.boolean().optional(),
    // keyed by the class enum: at most one entry per class
    modes: z
      .partialRecord(AgentActionClass, z.enum(["direct", "ask", "never"]))
      .meta({ maxProperties: AgentActionClass.options.length })
      .optional(),
  })
  .meta({ id: "AutonomyLevelBundle" });
export const AutonomySettings = z
  .strictObject({
    thresholds: z
      .strictObject({
        /** Weeks at the current level, all of them clean (shipped: 4). */
        weeks: z.int().min(1).max(26).optional(),
        /** Decisions on record in those weeks (shipped: 50). */
        min_decisions: z.int().min(0).max(100_000).optional(),
        /** Reversed share of the decisions, exclusive (shipped: 0.05, i.e. under 5%). */
        max_reversed_rate: z.number().min(0).max(1).optional(),
        /** Incidents allowed in those weeks (shipped: 0). */
        max_incidents: z.int().min(0).max(100).optional(),
      })
      .optional(),
    levels: z.array(AutonomyLevelBundle).max(4).optional(),
  })
  .meta({ id: "AutonomySettings" });
export type AutonomySettings = z.infer<typeof AutonomySettings>;

export const CompanySettings = z
  .object({
    /**
     * LOOP-BREAKER: the company's override of the platform's loop thresholds (each field absent or
     * null: the platform default; `api/loop-breaker.ts` `LoopThresholdsOverride`).
     */
    loop_breaker: z
      .object({
        enabled: z.boolean().nullable().optional(),
        repeat_limit: z.int().min(2).max(20).nullable().optional(),
        intent_repeat_limit: z.int().min(2).max(50).nullable().optional(),
        stall_minutes: z.int().min(1).max(240).nullable().optional(),
        similarity_pct: z.int().min(50).max(100).nullable().optional(),
      })
      .optional(),
    /**
     * HANDOFF-NO-DROPS: the company's expected minutes per step of a helper chain (each absent or
     * null: the platform default — card 45, render 30, run 20, waiting for a person 1440). Past
     * it with nothing moving, the requester is told the request is stuck.
     */
    /**
     * MONITORING-P1: how long the DEs' telemetry is kept (default 30 days) and the alert limits
     * (each absent or null: the default — a run with no event for 10 minutes is stuck; 6 posts
     * in 10 minutes are a storm).
     */
    monitoring: z
      .object({
        retention_days: z.int().min(1).max(365).nullable().optional(),
        stuck_minutes: z.int().min(2).max(240).nullable().optional(),
        post_storm_count: z.int().min(2).max(500).nullable().optional(),
        post_storm_minutes: z.int().min(1).max(240).nullable().optional(),
        /**
         * MONITORING-P2: a DE whose telemetry is on and that has a run queued or running is
         * `bridge_silent` after this many minutes without an event (default 30).
         */
        bridge_silent_minutes: z.int().min(5).max(1440).nullable().optional(),
      })
      .optional(),
    handoff_watch: z
      .object({
        enabled: z.boolean().nullable().optional(),
        card_minutes: z.int().min(5).max(10080).nullable().optional(),
        render_minutes: z.int().min(5).max(10080).nullable().optional(),
        run_minutes: z.int().min(5).max(1440).nullable().optional(),
        person_minutes: z.int().min(30).max(20160).nullable().optional(),
      })
      .optional(),
    /**
     * CLOSED-LOOPS (B3): the operator data every commitment's clock runs on. `types`: per request
     * type, its default due, accept-by, verify and TTL minutes and its self-fix / re-plan budgets
     * (a request with no due of its own and no default for its type is refused). `ladder`: the
     * escalation windows (second nudge, the owner's manager, each level up — every level's window
     * `up_shrink_pct` of the one below, never under `min_up_minutes`). `category_minutes`: how
     * long a decision a blocker raised may wait, per category. `policy_approver_membership_id`:
     * who decides POLICY blockers (absent: the company's owner). `cover`: who answers for a person
     * while they are away (absent or gone: the company's owner).
     */
    commitments: z
      .object({
        types: boundedRecord(
          z.object({
            due_minutes: z.int().min(5).max(43200).nullable().optional(),
            accept_minutes: z.int().min(1).max(10080).nullable().optional(),
            verify_minutes: z.int().min(5).max(43200).nullable().optional(),
            ttl_minutes: z.int().min(30).max(129600).nullable().optional(),
            max_attempts: z.int().min(0).max(20).nullable().optional(),
            max_replans: z.int().min(0).max(10).nullable().optional(),
          }),
          50,
          z
            .string()
            .max(64)
            .regex(/^[a-z][a-z0-9_]{0,63}$/),
        ).optional(),
        ladder: z
          .object({
            nudge_minutes: z.int().min(5).max(10080).nullable().optional(),
            manager_minutes: z.int().min(5).max(10080).nullable().optional(),
            up_minutes: z.int().min(5).max(10080).nullable().optional(),
            up_shrink_pct: z.int().min(10).max(100).nullable().optional(),
            min_up_minutes: z.int().min(5).max(1440).nullable().optional(),
          })
          .optional(),
        category_minutes: z
          .object({
            access: z.int().min(5).max(10080).nullable().optional(),
            service: z.int().min(5).max(10080).nullable().optional(),
            input: z.int().min(5).max(20160).nullable().optional(),
            conflict: z.int().min(5).max(10080).nullable().optional(),
            convergence: z.int().min(5).max(10080).nullable().optional(),
            policy: z.int().min(5).max(20160).nullable().optional(),
          })
          .optional(),
        policy_approver_membership_id: Uuid.nullable().optional(),
        cover: z
          .array(
            z.object({
              membership_id: Uuid,
              away_until: Timestamp,
              delegate_membership_id: Uuid.nullable().optional(),
            }),
          )
          .max(100)
          .optional(),
      })
      .optional(),
    /** CHART-DIMENSIONS: the org chart's default dimensions and per-dimension audience. */
    chart: ChartSettings.optional(),
    /**
     * FOUNDER-OS: the seat Founder OS shows as this company's head (its CEO card); null or absent:
     * the filled root seat with the most reports. A company manager sets it (Setup › Chart).
     */
    head_seat_id: Uuid.nullable().optional(),
    /** FOUNDER-OS: what crosses to a Founder OS holding the company (owner only; see type). */
    founder_sharing: FounderSharing.optional(),
    friday_report: z
      .object({
        generate_at: WallTime.optional(),
        auto_send: z.boolean().optional(),
        recipients_extra: z.array(Email).max(50).optional(),
        /**
         * Members who opted out of the report (default-on for owner, admin and client viewer;
         * S1-14 decision). Becomes a `membership` column in a later migration.
         */
        opted_out_membership_ids: z.array(Uuid).max(500).optional(),
      })
      .optional(),
    flags: boundedRecord(z.boolean()).optional(),
    /**
     * BOARD-RESULTS: the hours a channel may be posted at (company time; `*` is every channel
     * without its own row). Absent: 07:00–20:00. The posting-time learner only tests slots inside.
     */
    posting_windows: boundedRecord(
      z
        .strictObject({ start_hour: z.int().min(0).max(23), end_hour: z.int().min(1).max(24) })
        .refine((w) => w.end_hour > w.start_hour, "end_hour must be after start_hour"),
      20,
      z
        .string()
        .max(40)
        .regex(/^(\*|[a-z][a-z0-9_]{0,39})$/),
    ).optional(),
    /** B5-DECIDE: the classifier switches (`decide.settings.*`; absent = all off). */
    decide: DecideSettingsStored.optional(),
    /**
     * PERSISTENT-SCREENS: how long a screen waiting on a person ("needs you": a login, a 2FA code)
     * stays open before the request expires (minutes; default 30). An agent may ask for longer,
     * never shorter; a person may press Extend.
     */
    screens: z.strictObject({ needs_you_wait_min: z.int().min(5).max(240).optional() }).optional(),
    /**
     * Human-time cost rates (S1-11; 13 §time_entry "human-time cost rows"): an hourly rate in
     * minor units of the company's reporting currency per person membership, optionally only
     * for some time-entry kinds. The first matching entry applies; a person without one has no
     * cost rate and their time posts no `human_time` row.
     */
    human_time_rates: z
      .array(
        z.strictObject({
          membership_id: Uuid,
          hourly_minor: z.int().min(0).max(100_000_000),
          kinds: z.array(TimeEntryKind).min(1).max(8).optional(),
        }),
      )
      .max(500)
      .optional(),
    /**
     * M3-07 (OPEN-QUESTIONS 16; ADR-0021 §3): the company's model policy for gateway runs.
     * `residency` filters the gateway's model list to models served in that region (a model
     * the gateway lists no region for is excluded: fail closed); `default_model` is the model a
     * run gets when neither the run nor its agent profile names one; `allowed_models` narrows the
     * gateway's list. Per-role routing (`role_model_map`) replaces this later. MODEL-POLICY:
     * `role_defaults` is the company's default model per task kind (a pipeline role of the
     * role-to-model map: `lead`, `draft`, `review`, `verify`, …), preferred over `default_model`
     * for a run of that kind; like the default it is a preference the allow-list still filters.
     */
    model_policy: CompanyModelPolicy.optional(),
    /**
     * MODEL-KEYS-PLANS: how the company's model calls are paid. `ours` (the default when absent):
     * our gateway keys, billed through the wallet at the client price. `own`: the company's own
     * provider keys (Connections › API keys, one per provider), billed by its provider, never by
     * us — open only when its plan allows it (`cp.plan_model_access`). `fallback_to_ours`: a
     * call whose provider has no company key runs on ours (billed) instead of failing.
     */
    model_access: CompanyModelAccess.optional(),
    /**
     * TASK-FILES (Work OS `tenants.max_upload_mb` / `trash_retention_days`): the upload cap per
     * file in MB (default 25) and how long a trashed file stays restorable (default 30 days).
     */
    files: z
      .object({
        max_upload_mb: z.int().min(1).max(100).optional(),
        trash_retention_days: z
          .union([z.literal(7), z.literal(14), z.literal(30), z.literal(60), z.literal(90)])
          .optional(),
      })
      .optional(),
    /**
     * HARDEN-mfa: `require_two_factor_for_admins` makes the company's owners and admins set up
     * two-factor sign-in before they reach it (403 `mfa_enrollment_required` until they do).
     * Only an owner changes it (`companies.update`); agency operators always need it.
     * AGENT-SAFETY: `content_screen` is how external content reaching an agent run is screened
     * for instruction-shaped text (domain-agents `content-screen.ts`): `observe` (the default
     * when absent) records a verdict on the run timeline and never blocks; `off` skips it;
     * `enforce` refuses flagged content, and only where the install allows it
     * (`COS_CONTENT_SCREEN_ENFORCE=1`, off by default); elsewhere it records like observe.
     * SKILL-LEAK-GUARD: `skill_guard_mode` is what the skill-leak output guard does when an
     * agent's outgoing text matches a protected skill: `alert` (the default when absent) raises a
     * `skill_leak_suspected` alert and lets the write through; `block_and_alert` refuses the
     * write with a neutral message and raises the alert. `skill_guard_thresholds` tunes the
     * overlap rule (default 8 consecutive matching 5-grams, or 15% of one file's shingles).
     */
    security: z
      .strictObject({
        require_two_factor_for_admins: z.boolean().optional(),
        content_screen: ContentScreenMode.optional(),
        skill_guard_mode: SkillGuardMode.optional(),
        skill_guard_thresholds: z
          .strictObject({
            consecutive_shingles: z.int().min(3).max(200).optional(),
            file_ratio: z.number().min(0.02).max(1).optional(),
          })
          .optional(),
      })
      .optional(),
    /**
     * AGENT-SAFETY: skill-pack lifecycle. `archive_unused_after_days` archives (never deletes) a
     * pack no run has used for that many days; absent = off (the default).
     */
    skills: z
      .strictObject({ archive_unused_after_days: z.int().min(7).max(3650).optional() })
      .optional(),
    /**
     * D15 (MEMORY-HYGIENE 2026-10-30: ON by default; `enabled: false` switches it off): agents
     * write memory selectively. On: an agent's run writes only typed one-sentence memories with evidence
     * through CompanyOS (`POST agent/memory`), each with a why, no per-run cap (MEMORY-SKILL-2:
     * `budget_per_run` is still accepted from older settings and ignored); a lesson is served
     * only to its own seat until a person shares it; the run's container gets no Recall key, the run's
     * hand-back summary and agents' own messages are not sent to Recall. Off: as before.
     *
     * D14 (recommended, not approved): `hot_copy.enabled` keeps a per-agent hot copy of what
     * Recall delivers to it (recall/hot-copy.ts), used by a run only while Recall is down, and
     * queues a write Recall could not take for replay to the exact space it was aimed at. Off
     * (absent) by default: nothing changes.
     */
    memory: z
      .strictObject({
        agent_discipline: z
          .strictObject({
            enabled: z.boolean().optional(),
            /** MEMORY-SKILL-2: ignored (no per-run cap); kept so stored settings still parse. */
            budget_per_run: z.int().min(1).max(10).optional(),
          })
          .optional(),
        hot_copy: z.strictObject({ enabled: z.boolean().optional() }).optional(),
        /**
         * CAPTURE-LESS-NOISE: the company's override of the platform's capture policy
         * (`cp.memory_capture_setting`): which conversations go to Recall, by source. Absent: the
         * platform default (chat and task threads with a person on, run threads off).
         */
        capture: z
          .strictObject({
            chat: z.boolean().optional(),
            task_threads: z.boolean().optional(),
            runs: z.boolean().optional(),
          })
          .optional(),
        /**
         * RECALL-UPKEEP: Recall's dreams (nightly consolidation of every space: dedupe, merge,
         * rule proposals), as Recall's `PUT tenant/dream-schedule` takes them. Off (absent) by
         * default; `hour_utc` (default 2) is when they run; `auto_apply_topics` (default false)
         * lets topic tidying apply without a person, everything else waits for review. The
         * `recall.upkeep` job sends it to Recall (where Recall offers the door).
         */
        dreams: z
          .strictObject({
            enabled: z.boolean().optional(),
            hour_utc: z.int().min(0).max(23).optional(),
            auto_apply_topics: z.boolean().optional(),
          })
          .optional(),
      })
      .optional(),
    /**
     * COS-SLICE1 (docs/design/2026-10-28-chief-of-staff-and-assistants.md §2.5, §9.1): the
     * Chief of Staff's loop. A DE with `overrides.digest.enabled` on its lead profile is checked
     * every `interval_min` (5–60, default 15) and woken only when the delta is worth acting on, at
     * most `max_runs_per_hour` (default 6, two kept for immediate kinds) and `max_runs_per_day`
     * (default 40) digest runs; inside `quiet_hours` (default 19:00–08:00 and weekends; null: none)
     * only the immediate kinds wake it. `approval_wait_hours` (default 4) is when a waiting
     * approval is raised again; `max_effects_per_run` (default 10) caps the task creations and
     * handoffs one agent run may make (a seat's `settings.max_effects_per_run` overrides it);
     * `suggestions` (default on) turns the follow-up cards in chat on or off. A DE's
     * `overrides.digest` may override the loop's fields for itself.
     */
    /**
     * Notifications. AGENT-REPORTING: `reporting_email` false keeps reporting reminders out of
     * the digest email. INBOX-PREFS (founder-approved 2026-10-29): `default_preset` is the preset
     * a person without their own starts from (default `manager`); `nudge_hours` is how long a
     * waiting item waits before its one gentle reminder (default 4), and as long again before it
     * is handed on (`inbox_item.escalation_due`). An admin sets them (`companies.update`).
     */
    notifications: z
      .object({
        reporting_email: z.boolean().optional(),
        default_preset: NotificationPreset.optional(),
        nudge_hours: z.int().min(1).max(168).optional(),
      })
      .optional(),
    chief_of_staff: CosLoopSettings.extend({
      approval_wait_hours: z.int().min(1).max(72).optional(),
      max_effects_per_run: z.int().min(1).max(100).optional(),
      suggestions: z.boolean().optional(),
    })
      .extend(COS_MODE_SETTINGS)
      .optional(),
    /**
     * DE-INITIATE (founder-approved 2026-10-29): whether self-hosted DEs may start a thread or a
     * DM themselves. `enabled` (default on) turns it off company-wide; `off_for` lists the DEs
     * (membership ids) it is off for. `routines` declares each DE's routine posts (a key and the
     * channel, daily status and the like): once a day each, outside the one-a-day rule.
     * `quiet_hours` (default 19:00–08:00 and weekends, the company's zone; null: none) is when
     * nothing unprompted goes to a channel; a DM keeps its person's own quiet hours when set.
     */
    de_initiation: z
      .strictObject({
        enabled: z.boolean().optional(),
        off_for: z.array(Uuid).max(500).optional(),
        routines: z
          .array(
            z.strictObject({
              de_membership_id: Uuid,
              key: z
                .string()
                .max(64)
                .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/),
              channel_id: Uuid,
            }),
          )
          .max(200)
          .optional(),
        quiet_hours: CosQuietHours.nullable().optional(),
      })
      .optional(),
    /**
     * DE-BUDGET-PACE: when a DE's budget pace raises the bell and how much of its limit is held
     * back. `alert_at` (default 1.1): alert when the projected period spend reaches this multiple
     * of the limit, once `min_used` (default 0.2) of the limit is spent; `second_at` (default
     * 1.3): one more alert in the period when the pace worsens past it; `reserve` (default 0.01):
     * the last part of the limit no new run may start into. `for_de` overrides any of them per DE
     * (membership id).
     */
    budget_pace: CompanyBudgetPace.optional(),
    /**
     * HELPERS (docs/design/2026-10-30-helpers-visibility.md §2): how DEs are named. `role`
     * (default): the role is the name. `person`: a DE's optional person name shows before its
     * role ("Maya · Marketing Director") and the DE editor offers the field.
     */
    workforce: z.strictObject({ de_names: z.enum(["role", "person"]).optional() }).optional(),
    /** RUN-EFFORT: the effort tiers' models (admin-only; `runEffort.put`). */
    run_effort: RunEffortSettings.optional(),
    /**
     * CORE-FREEZE (behind `flags.core_freeze`): what a freeze does to queued outward sends —
     * `hold` (the default: kept queued, unsent, until the freeze is lifted) or `cancel`. A
     * founder decision pending (core plan §7 item 3); read when a freeze opens.
     */
    freeze: z.strictObject({ outbox: FreezeOutboxMode.optional() }).optional(),
    /** VOICE-CHOICES: the company's default stock voice (owner-set; `voice.settings.put`). */
    voice: z
      .strictObject({
        default_voice_id: z
          .string()
          .max(40)
          .regex(/^[a-z][a-z0-9-]{1,39}$/)
          .optional(),
      })
      .optional(),
    /**
     * GHL-PROVIDER (build order §3.1a): where an agent's `crm_*` tools go. `connector` (the
     * default until the native CRM's import switches it): the company's GoHighLevel connection,
     * through the connector adapter. `native`: the native CRM (increment 5); the connector tools
     * refuse a call and name the mode.
     */
    crm_mode: z.enum(["connector", "native"]).optional(),
    /**
     * CORE-AUTONOMY (core plan item 6; flag `settings.flags.earned_autonomy`, off by default):
     * the company's edits of the shipped autonomy levels and promotion thresholds
     * (services/api/src/domain/agent-policy/autonomy-levels.json). Absent: the shipped proposal.
     */
    autonomy: AutonomySettings.optional(),
  })
  .meta({ id: "CompanySettings" });
export type CompanySettings = z.infer<typeof CompanySettings>;

/** domain-kpi `OrgCalendarConfig` (camelCase inside the jsonb, as the engine reads it). */
export const OrgCalendarConfig = z
  .object({
    timezone: TimeZone,
    weekStartDay: z.int().min(1).max(7),
    fiscalYearStartMonth: z.int().min(1).max(12),
  })
  .meta({ id: "OrgCalendarConfig" });
export type OrgCalendarConfig = z.infer<typeof OrgCalendarConfig>;

/** Where the DE runs today; the stable runtime key is `(instance_id, profile_name)`. */
export const RuntimeRef = z
  .object({
    system: z.literal("hermes"),
    instance_id: z.string().min(1).max(256),
    profile_name: z.string().min(1).max(256),
    chat: z.object({ surface: ChatSurface, handle: z.string().max(256) }).optional(),
  })
  .meta({ id: "RuntimeRef" });
export type RuntimeRef = z.infer<typeof RuntimeRef>;

/**
 * WORKFORCE (2026-10-26): where a self-hosted worker runs — a Hermes profile in a container
 * managed by a ClawPanel instance. An agent (AG) carries it on its row; a self-hosted DE carries
 * it in its lead profile's `overrides.runtime` (next to `overrides.self_hosted`).
 */
export const WorkerRuntime = z
  .object({
    system: z.literal("hermes"),
    container: z.string().min(1).max(256),
    profile: z.string().min(1).max(256),
    clawpanel_instance: z.string().min(1).max(64).nullable(),
  })
  .meta({ id: "WorkerRuntime" });
export type WorkerRuntime = z.infer<typeof WorkerRuntime>;

export const SpendCursor = z.object({
  last_spend_log_at: Timestamp.optional(),
  last_log_id: z.string().optional(),
});
export type SpendCursor = z.infer<typeof SpendCursor>;

/** The provisioning states of a DE's LiteLLM key (S1-12). */
export const LitellmProvisioningState = z.enum([
  "reserved",
  "issued",
  "delivered",
  "failed",
  "expired",
]);

/**
 * `digital_employee.litellm_provisioning` (S1-12, server-only): one provisioning attempt of the
 * DE's LiteLLM keys, fenced by `litellm_provision_attempt`. The remote identity is stable (the
 * deterministic alias, its `-read` twin and the LiteLLM user of the same id), so a crashed or
 * uncertain attempt is reconciled by alias; `pending_deletes` are remote key hashes whose
 * compensating delete has not succeeded yet (retried by the reconciler, never dropped).
 */
export const LitellmProvisioning = z.object({
  state: LitellmProvisioningState,
  alias: z.string(),
  lease_until: Timestamp.nullable(),
  request: z.object({
    max_budget_minor: z.int(),
    currency: z.string(),
    models: z.array(z.string()),
  }),
  /** The agency user the issued key is bound to (only they may collect and acknowledge it). */
  recipient_user_id: z.string().nullable(),
  de_key_hash: z.string().nullable(),
  read_key_hash: z.string().nullable(),
  /** The puller's `ingest:cost` key issued with this attempt (revoked if the issuance expires). */
  ingest_key_id: z.string().nullable(),
  issued_expires_at: Timestamp.nullable(),
  delivered_at: Timestamp.nullable(),
  pending_deletes: z.array(z.string()),
  /**
   * Attempts whose remote keys may exist and are not accounted for (an uncertain create, a
   * superseded or failed runner, a listing that failed or returned untagged keys). Only keys
   * tagged with one of these attempts are ever swept; the reconciler retries until discovery
   * succeeded. `live_until`: until then the attempt's runner may still create a key (its lease
   * plus one LiteLLM call), so a clean discovery before it does not retire the marker.
   */
  sweep_attempts: z.array(z.object({ attempt: z.int(), live_until: Timestamp.nullable() })),
  /**
   * Orphan candidates the gateway reports no `created_at` for: key hash → when the orphan
   * sweep first saw it. Their grace period runs from then.
   */
  orphan_first_seen: z.record(z.string(), Timestamp).optional(),
  error: z.string().nullable(),
});
export type LitellmProvisioning = z.infer<typeof LitellmProvisioning>;

/**
 * `digital_employee.litellm_spend` (S1-12, server-only), fenced by `litellm_spend_version`:
 *   - the puller lease (`lease_owner`, `lease_until`);
 *   - the accounting `mode` and its coverage: in `spend_logs` the cursor is the boundary; in
 *     `key_info` `coverage_until` is the last moment the booked total (read from the ledger)
 *     equalled the observed cumulative one; `request_floor` excludes request rows already
 *     covered by daily deltas after a switch back;
 *   - `generation` (in every fallback meter identity), `generation_started_at` and
 *     `generation_base` (the cumulative total at the reset, counted as covered) +
 *     `discontinuity`: a cumulative total below the booked one (reset) is never silently
 *     absorbed;
 *   - `blocked`: items not booked (conflict, rejected, unmapped) that hold the cursor;
 *   - the last `/key/info` reading (the operator view's 80 % flag). Amounts are LiteLLM's USD.
 */
export const LitellmSpendState = z.object({
  lease_owner: z.string().nullable(),
  lease_until: Timestamp.nullable(),
  mode: z.enum(["spend_logs", "key_info"]),
  coverage_until: Timestamp.nullable(),
  request_floor: Timestamp.nullable(),
  generation: z.int(),
  generation_started_at: Timestamp.nullable(),
  generation_base: z.number(),
  discontinuity: z
    .object({ detected_at: Timestamp, booked_total: z.number(), observed_total: z.number() })
    .nullable(),
  blocked: z.array(z.object({ item_key: z.string(), reason: z.string() })),
  reading: z
    .object({
      max_budget: z.number().nullable(),
      total_spend: z.number(),
      at_budget_warning: z.boolean(),
      checked_at: Timestamp,
    })
    .nullable(),
});
export type LitellmSpendState = z.infer<typeof LitellmSpendState>;

export const OriginRef = z.object({ system: z.string(), id: z.string() });
export type OriginRef = z.infer<typeof OriginRef>;

/**
 * `metric_def.event_query` (13 §metric_def). `filter.payload` is an equality filter on top-level
 * payload fields, needed for the seeded catalog (`visibility = client`, `kind = supervision`,
 * `approval_status = approved`); see README "Contract decisions".
 *
 * `event_type` is one event type or the `time_entry.*` family (finding #4). The family is folded
 * to live rows first (`liveTimeRows`: latest revision, voids removed) and `filter.payload`
 * applies to the live row, so `sum:duration_seconds` counts approved, unvoided seconds once.
 * `sum:duration_seconds` is only meaningful on that family, and the family only takes it or
 * `count`.
 */
export { TIME_ENTRY_FAMILY };

export const EventQuery = z
  .object({
    event_type: z.union([DottedName, z.literal(TIME_ENTRY_FAMILY)]),
    filter: z
      .object({
        type: DottedName.optional(),
        attrs: boundedRecord(
          z.union([z.string().max(256), z.number(), z.boolean()]),
          20,
        ).optional(),
        payload: boundedRecord(
          z.union([z.string().max(256), z.number(), z.boolean()]),
          20,
        ).optional(),
      })
      .optional(),
    measure: EventMeasure,
  })
  .superRefine((q, ctx) => {
    const family = q.event_type === TIME_ENTRY_FAMILY;
    if (family && q.measure !== "sum:duration_seconds" && q.measure !== "count")
      ctx.addIssue({
        code: "custom",
        path: ["measure"],
        message: "time_entry.* takes sum:duration_seconds or count",
      });
    if (!family && q.measure === "sum:duration_seconds")
      ctx.addIssue({
        code: "custom",
        path: ["measure"],
        message: "sum:duration_seconds needs event_type time_entry.*",
      });
    if (!family && q.event_type.startsWith("time_entry."))
      ctx.addIssue({
        code: "custom",
        path: ["event_type"],
        message: "query time entries as time_entry.* (live rows)",
      });
  })
  .meta({ id: "EventQuery" });
export type EventQuery = z.infer<typeof EventQuery>;

/** A filter value a task metric compares a field with (a select key, a number, a checkbox). */
const TaskMetricValue = z.union([z.string().max(256), z.number(), z.boolean()]);

/**
 * T3-TASK-ROLLUPS: `metric_def.task_query`, a metric counted from one board's tasks by the
 * `task_metric.snapshot` job. `measure`: `count` the matching tasks, or `sum` / `avg` a number
 * field (`field_id`: a number, currency, formula or rollup field of the board). The day a task
 * counts on is its `date` (`completed_at`, `due_at`, `created_at`, or `field` = the date field
 * `date_field_id`), in company time. `filter`: status keys (any of), `done` (finished or not),
 * a field equal to a value, a tag; all given parts must hold. Generic: nothing here knows what a
 * board's tasks are.
 */
export const TaskMetricQuery = z
  .strictObject({
    board_id: Uuid,
    measure: z.enum(["count", "sum", "avg"]),
    field_id: Uuid.nullable().default(null),
    date: z.enum(["completed_at", "due_at", "created_at", "field"]),
    date_field_id: Uuid.nullable().default(null),
    filter: z
      .strictObject({
        statuses: z.array(text(LIMITS.key)).min(1).max(50).optional(),
        done: z.boolean().optional(),
        field: z.strictObject({ field_id: Uuid, equals: TaskMetricValue }).optional(),
        tag_id: Uuid.optional(),
      })
      .default({}),
  })
  .superRefine((q, ctx) => {
    if (q.measure !== "count" && !q.field_id)
      ctx.addIssue({
        code: "custom",
        path: ["field_id"],
        message: `${q.measure} needs a number field`,
      });
    if ((q.date === "field") !== (q.date_field_id != null))
      ctx.addIssue({
        code: "custom",
        path: ["date_field_id"],
        message: "required iff date = field",
      });
  })
  .meta({ id: "TaskMetricQuery" });
export type TaskMetricQuery = z.infer<typeof TaskMetricQuery>;

/** `cost_event.source_ref`: provenance, plus the free-text note of a manual post or reversal. */
export const SourceRef = z
  .object({
    system: z.string(),
    kind: z.string().optional(),
    id: z.string(),
    note: z.string().optional(),
  })
  .meta({ id: "SourceRef" });
export type SourceRef = z.infer<typeof SourceRef>;

export const TimeEntryStats = z.object({
  activity_pct: z.number().optional(),
  manual_seconds: z.int().optional(),
  pending_seconds: z.int().optional(),
  rejected_seconds: z.int().optional(),
  over_cap_seconds: z.int().optional(),
});
export type TimeEntryStats = z.infer<typeof TimeEntryStats>;

export const TimeEntryDims = z.object({ client: z.string().optional() });
export type TimeEntryDims = z.infer<typeof TimeEntryDims>;

// Identity and org ------------------------------------------------------------------------------

export const Company = z
  .object({
    id: Uuid,
    slug: Slug,
    name: z.string(),
    kind: CompanyKind,
    settings: CompanySettings,
    calendar_config: OrgCalendarConfig,
    reporting_currency: Currency,
    /** MANAGED-POLICY: `managed` = the company requests agents, its agency adds them. */
    agent_mode: CompanyAgentMode,
    ...stamps,
  })
  .meta({ id: "Company" });
export type Company = z.infer<typeof Company>;

export const Membership = z
  .object({
    company_id: Uuid,
    id: Uuid,
    user_id: Uuid.nullable(),
    kind: MembershipKind,
    status: MembershipStatus,
    display_name: z.string(),
    avatar_key: z.string().nullable(),
    joined_at: Timestamp,
    owner_membership_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Membership" });
export type Membership = z.infer<typeof Membership>;

/**
 * A pending person membership (S1-06 review #1): no access until the invitee, signed in with a
 * verified email equal to `email`, accepts it with the mailed token (stored only as a hash).
 */
export const Invitation = z
  .object({
    company_id: Uuid,
    id: Uuid,
    email: Email,
    display_name: z.string(),
    roles: z.array(InvitationRole),
    status: InvitationStatus,
    version: z.int().min(1),
    sent_at: Timestamp.nullable(),
    expires_at: Timestamp,
    membership_id: Uuid.nullable(),
    accepted_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Invitation" });
export type Invitation = z.infer<typeof Invitation>;

export const Role = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    default_metric_keys: z.array(z.string()),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Role" });
export type Role = z.infer<typeof Role>;

export const Seat = z
  .object({
    company_id: Uuid,
    id: Uuid,
    role_id: Uuid,
    title: z.string(),
    supervisor_seat_id: Uuid.nullable(),
    staff: z.boolean().describe("A staff (assistant) seat: drawn at the side of its supervisor"),
    kind: SeatKind,
    status: SeatStatus,
    origin: SeatOrigin,
    origin_ref: OriginRef.nullable(),
    settings: z.record(z.string(), z.unknown()),
    de_fill: SeatDeFill.nullable().describe(
      "DE-SEAT-OWNER: what a DE on the seat becomes without a person (null: never set)",
    ),
    version: z.int().min(1),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Seat" });
export type Seat = z.infer<typeof Seat>;

export const SeatAssignment = z
  .object({
    company_id: Uuid,
    id: Uuid,
    seat_id: Uuid,
    membership_id: Uuid,
    membership_kind: SeatHolderKind,
    share: Decimal,
    active_from: Timestamp,
    active_to: Timestamp.nullable(),
    capacity: SeatAssignmentCapacity.nullable().describe(
      "DE-SEAT-OWNER: a DE's assignment as assistant, acting owner or owner (null: a person's, or before the flag)",
    ),
    ...actor,
    ...stamps,
  })
  .meta({ id: "SeatAssignment" });
export type SeatAssignment = z.infer<typeof SeatAssignment>;

/** `litellm_read_key_enc` / `_kid` are sealed secrets and never leave the server. */
export const DigitalEmployee = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    template_key: z.string(),
    runtime_ref: RuntimeRef.nullable(),
    /** Phase 2 increment 6: `manual`, `deploy` (the opt-in to Hermes) or `local_pinned`. */
    runtime_ref_source: RuntimeRefSource.nullable(),
    litellm_key_alias: z.string().nullable(),
    spend_cursor: SpendCursor.nullable(),
    /** Why the last provisioning step (the LiteLLM key) failed; null when none did. */
    provisioning_error: z.string().nullable(),
    status: DigitalEmployeeStatus,
    /** WALLET-P1: why it is paused (`wallet` = no credit); null when it is not paused. */
    pause_reason: DePauseReason.nullable(),
    started_on: IsoDate.nullable(),
    lead_profile_id: Uuid.nullable(),
    pipeline_ref: z.string().nullable(),
    egress_allowlist: z.array(z.string()),
    max_concurrency: z.int().min(1).max(32),
    hired_from_crew_membership_id: Uuid.nullable(),
    // DC-01: the mirror of the current confirmed deploy (ADR-0018 §4)
    template_version_id: Uuid.nullable(),
    bundle_sha: z.string().nullable(),
    deploy_ref: Uuid.nullable(),
    verification_state: VerificationState,
    last_drift_audit_at: Timestamp.nullable(),
    /** AGENT-MENTION: removed by a manager (retired, keys revoked); hidden from the roster. */
    removed_at: Timestamp.nullable(),
    /** WF-POLISH: what the DE is (its operating profile, or the Custom add flow). */
    role: z.string().nullable(),
    goal: z.string().nullable(),
    backstory: z.string().nullable(),
    standing_orders: z.string().nullable(),
    /**
     * DE-PROFILE-LIVE: the shipped default of those four fields (set by the profile sync, never
     * by an edit) and when; null when none was shipped. The Overview shows where the profile
     * differs from it and resets a field to it.
     */
    profile_default: z
      .object({
        role: z.string().nullable(),
        goal: z.string().nullable(),
        backstory: z.string().nullable(),
        standing_orders: z.string().nullable(),
      })
      .nullable(),
    profile_default_at: Timestamp.nullable(),
    /** TEMPLATES-T4: the client overlay it was deployed from a role with. */
    overlay_id: Uuid.nullable(),
    role_version_id: Uuid.nullable(),
    role_skill_keys: z.array(z.string()),
    /** TEMPLATES-T4: probation at Draft level (decision 3); null when never on probation. */
    probation_started_at: Timestamp.nullable(),
    probation_until: Timestamp.nullable(),
    probation_tasks_required: z.int().min(0).nullable(),
    probation_tasks_done: z.int().min(0),
    probation_ended_at: Timestamp.nullable(),
    /** HELPERS: an optional person name before the role (`settings.workforce.de_names`). */
    person_name: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "DigitalEmployee" });
export type DigitalEmployee = z.infer<typeof DigitalEmployee>;

/** CHART-HOWIWORK: one saved version of a DE's "How I work" (append-only; newest = current). */
export const DeProcessVersion = z
  .object({
    company_id: Uuid,
    de_membership_id: Uuid,
    version: z.int().min(1),
    text: z.string().nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "DeProcessVersion" });
export type DeProcessVersion = z.infer<typeof DeProcessVersion>;

/** Key metadata; `key_hash` never leaves the server and the plaintext is returned once. */
export const AgentKey = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: AgentKeyKind,
    membership_id: Uuid,
    /** Null only for `service` keys (M1-02). */
    seat_id: Uuid.nullable(),
    key_prefix: z.string().length(12),
    scopes: z.array(AgentKeyScope),
    /** Ingest rule 7: items older than 90 days are accepted (operator-issued only). */
    backfill: z.boolean(),
    /** Meter namespaces the key may claim cost meter identities in (S1-08 review #1). */
    meter_namespaces: z.array(z.string()),
    /** M3-04 (D-16): the time source of a source-scoped key; null for a DE's keys. */
    source: IngestKeySource.nullable(),
    status: AgentKeyStatus,
    last_used_at: Timestamp.nullable(),
    expires_at: Timestamp.nullable(),
    revoked_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AgentKey" });
export type AgentKey = z.infer<typeof AgentKey>;

export const ExternalIdentity = z
  .object({
    company_id: Uuid,
    id: Uuid,
    system: z.string(),
    kind: ExternalIdentityKind,
    external_id: z.string(),
    membership_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    client_ref: z.string().nullable(),
    valid_from: IsoDate,
    valid_to: IsoDate.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "ExternalIdentity" });
export type ExternalIdentity = z.infer<typeof ExternalIdentity>;

// Teams, org chart, responsibilities, templates, webhooks (M1-02) -------------------------------

/** `seat_edge.policy` (02): limits carried by an edge of the org chart. */
export const SeatEdgePolicy = z
  .strictObject({
    /** Delegation depth allowed through this edge (05 §Delegation rule 6; default 2). */
    max_depth: z.int().min(1).max(10).optional(),
    /** Budget a delegation through this edge may reserve, in micros of the company currency. */
    budget_micros: z.int().min(0).optional(),
    /** Task kinds (board ids or free labels) the edge allows; absent = all. */
    allowed_task_kinds: z.array(z.string().max(LIMITS.key)).max(LIMITS.list).optional(),
  })
  .meta({ id: "SeatEdgePolicy" });
export type SeatEdgePolicy = z.infer<typeof SeatEdgePolicy>;

/** The task a `recurring_work` row materialises (02 §recurring_work). Text may use `{period}`. */
export const TaskTemplate = z
  .strictObject({
    title: text(LIMITS.name),
    description: z.string().max(LIMITS.longText).optional(),
    /** 0 none, 1 urgent … 4 low (the `task.priority` scale, M2-01). */
    priority: z.int().min(0).max(4).optional(),
    /** Assignee seats; default the responsibility's seat. */
    assignee_seat_ids: z.array(Uuid).max(20).optional(),
    checklist: z.array(text(LIMITS.name)).max(LIMITS.list).optional(),
    /** Due this many days after materialisation. */
    due_in_days: z.int().min(0).max(366).optional(),
  })
  .meta({ id: "TaskTemplate" });
export type TaskTemplate = z.infer<typeof TaskTemplate>;

/** A key inside a template spec that other entries reference (`seat: "lead"`). */
export const TemplateKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(LIMITS.key);

/**
 * `template.spec` (02 §template, 06 §Templates): what `POST templates/{id}/apply` creates in one
 * transaction. Entries reference each other by `key`; text may use `{{param}}` placeholders
 * filled from the apply request. Validation of references and placeholders is M1-06's.
 */
export const TemplateSpec = z
  .strictObject({
    version: z.literal(1),
    params: z
      .array(
        z.strictObject({
          key: TemplateKey,
          label: text(LIMITS.name),
          required: z.boolean().optional(),
          default: z.string().max(LIMITS.name).optional(),
        }),
      )
      .max(20)
      .optional(),
    roles: z
      .array(
        z.strictObject({
          key: TemplateKey,
          name: text(LIMITS.name),
          description: z.string().max(LIMITS.text).optional(),
        }),
      )
      .max(50)
      .optional(),
    teams: z
      .array(
        z.strictObject({
          key: TemplateKey,
          name: text(LIMITS.name),
          parent: TemplateKey.optional(),
          lead_seat: TemplateKey.optional(),
        }),
      )
      .max(50)
      .optional(),
    seats: z
      .array(
        z.strictObject({
          key: TemplateKey,
          role: TemplateKey,
          title: text(LIMITS.name),
          supervisor: TemplateKey.optional(),
          team: TemplateKey.optional(),
        }),
      )
      .max(LIMITS.list)
      .optional(),
    responsibilities: z
      .array(
        z.strictObject({
          seat: TemplateKey,
          name: text(LIMITS.name),
          description: z.string().max(LIMITS.text).optional(),
          metric_keys: z.array(z.string().max(LIMITS.key)).max(20).optional(),
          recurring: z
            .array(
              z.strictObject({
                schedule: z.string().max(500),
                board: TemplateKey.optional(),
                task: TaskTemplate,
              }),
            )
            .max(20)
            .optional(),
        }),
      )
      .max(200)
      .optional(),
    spaces: z
      .array(
        z.strictObject({
          key: TemplateKey,
          name: text(LIMITS.name),
          kind: z.enum(["team", "project", "client"]),
          team: TemplateKey.optional(),
          channels: z
            .array(z.strictObject({ name: text(LIMITS.name), is_private: z.boolean().optional() }))
            .max(50)
            .optional(),
          boards: z
            .array(
              z.strictObject({
                key: TemplateKey,
                name: text(LIMITS.name),
                statuses: z
                  .array(
                    z.strictObject({
                      key: TemplateKey,
                      name: text(LIMITS.name),
                      done: z.boolean(),
                    }),
                  )
                  .max(30)
                  .optional(),
                /**
                 * W1-FIXES (T1 review): the board's custom fields, created with it (a select's
                 * `choices` by name). Reference types (formula, relation, rollup) are not
                 * templated: they name other rows.
                 */
                fields: z
                  .array(
                    z.strictObject({
                      name: text(LIMITS.name),
                      type: FieldType.exclude(["formula", "relation", "rollup"]),
                      choices: z.array(text(LIMITS.name)).max(50).optional(),
                    }),
                  )
                  .max(30)
                  .optional(),
                /**
                 * W1-FIXES: the board's views, created with it. `date_field` (calendar,
                 * timeline): `due_at`, `start_at` or the name of one of `fields` of type date.
                 */
                views: z
                  .array(
                    z.strictObject({
                      name: text(LIMITS.name),
                      kind: ViewKind,
                      date_field: z.string().max(LIMITS.name).optional(),
                      is_default: z.boolean().optional(),
                    }),
                  )
                  .max(10)
                  .optional(),
                tasks: z.array(TaskTemplate).max(LIMITS.list).optional(),
              }),
            )
            .max(20)
            .optional(),
        }),
      )
      .max(20)
      .optional(),
  })
  .meta({ id: "TemplateSpec" });
export type TemplateSpec = z.infer<typeof TemplateSpec>;

/** `webhook.filter`: narrows the subscribed types to subjects; absent keys match everything. */
export const WebhookFilter = z
  .strictObject({
    subject_kinds: z.array(z.string().max(LIMITS.key)).max(50).optional(),
    subject_ids: z.array(Uuid).max(LIMITS.list).optional(),
    /**
     * WOS-BUNDLE (Work OS `webhooks.container_id`): only events inside this space or board — the
     * container itself, its boards, their tasks and views. Absent: the whole company.
     */
    container: z.strictObject({ kind: z.enum(["space", "board"]), id: Uuid }).optional(),
  })
  .meta({ id: "WebhookFilter" });
export type WebhookFilter = z.infer<typeof WebhookFilter>;

export const Team = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    parent_team_id: Uuid.nullable(),
    lead_seat_id: Uuid.nullable(),
    composition: TeamComposition,
    human_owner_seat_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Team" });
export type Team = z.infer<typeof Team>;

export const TeamMember = z
  .object({
    company_id: Uuid,
    team_id: Uuid,
    seat_id: Uuid,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "TeamMember" });
export type TeamMember = z.infer<typeof TeamMember>;

/** HELPERS-2: a DE's use of a helper it does not lead (`cell.agent_use`). */
export const AgentUse = z
  .object({
    company_id: Uuid,
    agent_id: Uuid,
    de_membership_id: Uuid,
    source: AgentUseSource,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "AgentUse" });
export type AgentUse = z.infer<typeof AgentUse>;

/**
 * HELPERS: the price of a requested helper, as the billing side (wp/SKILLSMITH-OPPORTUNITIES)
 * quotes it: an amount in minor units, its currency, one-off or monthly, an optional note and the
 * invoice it was sent as.
 */
export const HelperQuote = z
  .strictObject({
    amount_minor: z.int().min(0).max(100_000_000_00),
    currency: z
      .string()
      .length(3)
      .regex(/^[A-Z]{3}$/),
    interval: z.enum(["one_time", "month"]),
    note: z.string().max(2000).nullable().optional(),
    invoice_ref: z.string().max(200).nullable().optional(),
  })
  .meta({ id: "HelperQuote" });
export type HelperQuote = z.infer<typeof HelperQuote>;

/** WORKFORCE (2026-10-26): an agent (AG) — `cell.workforce_agent`. */
export const WorkforceAgent = z
  .object({
    company_id: Uuid,
    id: Uuid,
    slug: z.string(),
    name: z.string(),
    team_id: Uuid.nullable(),
    runtime: WorkerRuntime.nullable(),
    reports_to_membership_id: Uuid,
    reports_to_kind: WorkforceAgentReportsToKind,
    status: WorkforceAgentStatus,
    last_activity_at: Timestamp.nullable(),
    last_activity: z.string().nullable(),
    /** WF-POLISH: what it does (its operating profile, or the create dialog). */
    role: z.string().nullable(),
    goal: z.string().nullable(),
    backstory: z.string().nullable(),
    responsibilities: z.array(z.string()),
    standing_orders: z.string().nullable(),
    /** HELPERS: the skills it runs, by key (chips on its card). */
    skills: z.array(z.string()),
    ...actor,
    ...stamps,
  })
  .meta({ id: "WorkforceAgent" });
export type WorkforceAgent = z.infer<typeof WorkforceAgent>;

/**
 * MANAGED-POLICY: a request for a digital employee (`de`) or an agent (`ag`) in a managed
 * company — `cell.agent_request`. The agency's operators fulfil or decline it.
 */
export const AgentRequest = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: AgentRequestKind,
    role: z.string(),
    template_key: z.string().nullable(),
    note: z.string().nullable(),
    requested_by_membership_id: Uuid,
    state: AgentRequestState,
    resolution_note: z.string().nullable(),
    resolved_by_user_id: Uuid.nullable(),
    resolved_at: Timestamp.nullable(),
    fulfilled_ref_id: Uuid.nullable(),
    /** HELPERS: the DE a helper request is for (kind `helper`; null otherwise). */
    de_membership_id: Uuid.nullable(),
    /** HELPERS: a helper request's stage (null for other kinds). */
    stage: HelperRequestStage.nullable(),
    stage_changed_at: Timestamp.nullable(),
    /** HELPERS: the quote the client approves (set with stage `quoted`). */
    quote: HelperQuote.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AgentRequest" });
export type AgentRequest = z.infer<typeof AgentRequest>;

export const SeatEdge = z
  .object({
    company_id: Uuid,
    id: Uuid,
    from_seat_id: Uuid,
    to_seat_id: Uuid,
    kind: SeatEdgeKind,
    policy: SeatEdgePolicy,
    ...actor,
    ...stamps,
  })
  .meta({ id: "SeatEdge" });
export type SeatEdge = z.infer<typeof SeatEdge>;

export const Responsibility = z
  .object({
    company_id: Uuid,
    id: Uuid,
    seat_id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    weight: Decimal,
    kpi_metric_ids: z.array(Uuid),
    status: ResponsibilityStatus,
    ...actor,
    ...stamps,
  })
  .meta({ id: "Responsibility" });
export type Responsibility = z.infer<typeof Responsibility>;

/**
 * CHART-KB (02 §goal): a goal a seat owns; `kind = priority` is a quarterly priority (06 §Quarterly
 * priorities, the seat's keystone on the chart) with `period_key` its quarter.
 */
export const Goal = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: GoalKind,
    seat_id: Uuid,
    team_id: Uuid.nullable(),
    title: z.string(),
    due_at: Timestamp.nullable(),
    period_key: PeriodKey.nullable(),
    metric_def_id: Uuid.nullable(),
    target: Decimal.nullable(),
    state: GoalState,
    track: GoalTrack,
    flagged_to_portfolio: z.boolean(),
    portfolio_link_ref: z.string().nullable(),
    vision_page_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Goal" });
export type Goal = z.infer<typeof Goal>;

export const GoalMilestone = z
  .object({
    company_id: Uuid,
    id: Uuid,
    goal_id: Uuid,
    title: z.string(),
    due_at: Timestamp.nullable(),
    done_at: Timestamp.nullable(),
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "GoalMilestone" });
export type GoalMilestone = z.infer<typeof GoalMilestone>;

export const RecurringWork = z
  .object({
    company_id: Uuid,
    id: Uuid,
    responsibility_id: Uuid,
    schedule: z.string(),
    board_id: Uuid.nullable(),
    task_template: TaskTemplate,
    status: RecurringWorkStatus,
    next_run_at: Timestamp.nullable(),
    last_materialised_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "RecurringWork" });
export type RecurringWork = z.infer<typeof RecurringWork>;

export const Template = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    spec: TemplateSpec,
    version: z.int().min(1),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Template" });
export type Template = z.infer<typeof Template>;

/** `secret_enc` / `secret_kid` never leave the server; the secret is returned once at creation. */
export const Webhook = z
  .object({
    company_id: Uuid,
    id: Uuid,
    url: z.string(),
    description: z.string().nullable(),
    types: z.array(z.string()),
    filter: WebhookFilter,
    status: WebhookStatus,
    disabled_at: Timestamp.nullable(),
    disabled_reason: z.string().nullable(),
    /** WOS-BUNDLE: events in a row whose delivery exhausted its retries (5 pause the hook). */
    consecutive_failures: z.int().min(0),
    paused_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Webhook" });
export type Webhook = z.infer<typeof Webhook>;

export const WebhookDelivery = z
  .object({
    company_id: Uuid,
    id: Uuid,
    webhook_id: Uuid,
    event_id: Uuid,
    event_occurred_at: Timestamp,
    event_type: z.string(),
    status: WebhookDeliveryStatus,
    attempts: z.int().min(0),
    response_status: z.int().nullable(),
    error: z.string().nullable(),
    next_attempt_at: Timestamp.nullable(),
    delivered_at: Timestamp.nullable(),
    ...stamps,
  })
  .meta({ id: "WebhookDelivery" });
export type WebhookDelivery = z.infer<typeof WebhookDelivery>;

/**
 * WEBHOOKS-IN (design 2026-10-27 §2.1): an incoming webhook. Its secret never leaves the server but
 * once (create, rotate). A delivery becomes a task for the target DE (for `for_agent_id`, an AG
 * reporting to it), on `board_id` or the company's first team board.
 */
export const InboundWebhook = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    verification: InboundWebhookVerification,
    target_membership_id: Uuid,
    target_kind: z.literal("digital_employee"),
    for_agent_id: Uuid.nullable(),
    board_id: Uuid.nullable(),
    /** CIDRs a sender may post from; empty: any address. */
    allowed_cidrs: z.array(z.string()),
    max_bytes: z.int(),
    rate_per_minute: z.int(),
    payload_retention_days: z.int(),
    screen_mode: InboundWebhookScreenMode,
    status: InboundWebhookStatus,
    paused_at: Timestamp.nullable(),
    paused_reason: z.string().nullable(),
    disabled_at: Timestamp.nullable(),
    last_received_at: Timestamp.nullable(),
    /** The previous secret still verifies until then (24 h after a rotation). */
    previous_until: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "InboundWebhook" });
export type InboundWebhook = z.infer<typeof InboundWebhook>;

/** The content screen's verdict on a delivery at receipt (design §5.1). */
export const InboundDeliveryScreen = z
  .strictObject({
    verdict: z.enum(["clean", "flagged", "off"]),
    score: z.number(),
    rules: z.array(z.string()),
    /** Flagged where enforce applies: the task is made with the data withheld. */
    quarantined: z.boolean(),
  })
  .meta({ id: "InboundDeliveryScreen" });
export type InboundDeliveryScreen = z.infer<typeof InboundDeliveryScreen>;

/** One request to an incoming webhook (design §2.3). The body is served by `getDelivery` only. */
export const InboundDelivery = z
  .object({
    company_id: Uuid,
    id: Uuid,
    hook_id: Uuid,
    received_at: Timestamp,
    source_ip: z.string().nullable(),
    verification: InboundDeliveryVerification,
    dedupe_key: z.string().nullable(),
    bytes: z.int().min(0),
    content_type: z.string().nullable(),
    status: InboundDeliveryStatus,
    reject_reason: z.string().nullable(),
    screen: InboundDeliveryScreen.nullable(),
    body_purged_at: Timestamp.nullable(),
    /** The task the delivery became. */
    task_id: Uuid.nullable(),
    replayed_from: Uuid.nullable(),
    sample: z.boolean(),
    ...stamps,
  })
  .meta({ id: "InboundDelivery" });
export type InboundDelivery = z.infer<typeof InboundDelivery>;

// Measurement ----------------------------------------------------------------------------------

export const MetricDef = z
  .object({
    company_id: Uuid,
    id: Uuid,
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    unit: MetricUnit,
    direction: MetricDirection,
    aggregation: MetricAggregation,
    normalization: MetricNormalization.nullable(),
    cadence: MetricCadence.nullable(),
    source: MetricSource,
    event_query: EventQuery.nullable(),
    formula: z.string().nullable(),
    /** T3-TASK-ROLLUPS: what a `task` metric counts on its board; null for other sources. */
    task_query: TaskMetricQuery.nullable(),
    visibility: Visibility,
    gap_policy: GapPolicy,
    /** Archived definitions keep their data; they cannot be added to seats or written to. */
    archived_at: Timestamp.nullable(),
    /** AGENT-REPORTING: an agent's values wait for a person's review (13 §Reporting duties). */
    needs_review: z.boolean(),
    /** TEAM-KPI: the metric's own warning bands; null = the company's fixed bands. */
    bands: MetricBands.nullable(),
    /** PLATFORM-OPERATOR: the gate spec when the metric is a gate; null = an ordinary metric. */
    gate: MetricGate.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "MetricDef" });
export type MetricDef = z.infer<typeof MetricDef>;

export const SeatMetric = z
  .object({
    company_id: Uuid,
    seat_id: Uuid,
    metric_def_id: Uuid,
    kpi_weight: Decimal,
    goal_weight: Decimal,
    tracking_mode: TrackingMode,
    penalty_only: z.boolean(),
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "SeatMetric" });
export type SeatMetric = z.infer<typeof SeatMetric>;

const subject = { subject_kind: SubjectKind, subject_id: Uuid };

/**
 * TEAM-KPI (founder-approved 2026-10-21): a metric assigned to a team. `rollup` is where its
 * value comes from: `none` the team's own observations (entered, imported, or an event metric's
 * member events), `sum` / `average` its member seats' values.
 */
export const TeamMetric = z
  .object({
    company_id: Uuid,
    team_id: Uuid,
    metric_def_id: Uuid,
    rollup: TeamRollup,
    kpi_weight: Decimal,
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "TeamMetric" });
export type TeamMetric = z.infer<typeof TeamMetric>;

/** KPI-VISIBILITY (0058; 05 §KPI visibility): a grant row (`kpi_visibility_grant`). */
export const KpiVisibilityGrant = z
  .object({
    company_id: Uuid,
    id: Uuid,
    principal_kind: KpiPrincipalKind,
    principal_id: Uuid,
    scopes: z.array(KpiScope),
    groups: z.array(KpiGroup),
    ...actor,
    ...stamps,
  })
  .meta({ id: "KpiVisibilityGrant" });
export type KpiVisibilityGrant = z.infer<typeof KpiVisibilityGrant>;

export const TargetRule = z
  .object({
    company_id: Uuid,
    id: Uuid,
    metric_def_id: Uuid,
    ...subject,
    period_type: PeriodType,
    value: Decimal,
    value_max: Decimal.nullable(),
    effective_from: IsoDate,
    effective_to: IsoDate.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "TargetRule" });
export type TargetRule = z.infer<typeof TargetRule>;

export const Observation = z
  .object({
    company_id: Uuid,
    id: Uuid,
    metric_def_id: Uuid,
    ...subject,
    period_type: PeriodType,
    period_key: PeriodKey,
    period_starts_on: IsoDate,
    value: Decimal.nullable(),
    status: ObservationStatus,
    source: ObservationSource,
    source_event_id: z.string(),
    source_event_ids: z.array(Uuid).nullable(),
    supersedes_id: Uuid.nullable(),
    tombstone: z.boolean(),
    note: z.string().nullable(),
    entered_by_membership_id: Uuid.nullable(),
    ...actor,
    created_at: Timestamp,
    provenance: ObservationProvenance.nullable(),
  })
  .meta({ id: "Observation" });
export type Observation = z.infer<typeof Observation>;

/**
 * A metric definition as scored at publication (S1-10 review #7): what reproduces a revision's
 * math — direction, aggregation, cadence, formula — whatever the definition says today.
 * `visibility` is the effective one (agency when any formula input is agency, review #2).
 */
export const PublicationMetricDef = z
  .object({
    id: Uuid,
    key: z.string(),
    name: z.string(),
    unit: MetricUnit,
    direction: MetricDirection,
    aggregation: MetricAggregation,
    normalization: MetricNormalization.nullable(),
    cadence: MetricCadence.nullable(),
    source: MetricSource,
    formula: z.string().nullable(),
    visibility: Visibility,
    gap_policy: GapPolicy,
    /** TEAM-KPI: the bands in force (absent in snapshots frozen before 2026-10-21). */
    bands: MetricBands.nullable().optional(),
  })
  .meta({ id: "PublicationMetricDef" });
export type PublicationMetricDef = z.infer<typeof PublicationMetricDef>;

const configFields = {
  seat_metrics: z.array(
    z.object({
      seat_id: Uuid,
      metric_def_id: Uuid,
      kpi_weight: z.number(),
      goal_weight: z.number(),
      tracking_mode: TrackingMode,
      penalty_only: z.boolean(),
    }),
  ),
  target_rules: z.array(
    z.object({
      id: Uuid,
      metric_def_id: Uuid,
      ...subject,
      period_type: PeriodType,
      value: z.number(),
      value_max: z.number().nullable(),
      effective_from: IsoDate,
      effective_to: IsoDate.nullable(),
    }),
  ),
  policy: z.object({
    kpi_thresholds: z.array(z.number()),
    goal_thresholds: z.array(z.number()),
    attainment_cap: z.number(),
    insufficient_data_cutoff: z.number(),
  }),
  /** The definitions the scores read (the seat's metrics and their formula inputs). */
  metric_defs: z.array(PublicationMetricDef).optional(),
};

/** One child of a company publication: the DE seat and the configuration of its score. */
export const PublicationChild = z
  .object({ seat_id: Uuid, title: z.string(), config: z.object(configFields) })
  .meta({ id: "PublicationChild" });

/**
 * Seat metrics, targets, definitions and policy in force at period end (13
 * §scorecard_publication). A company publication also freezes its children (review #6): the DE
 * seats it rolled up and the configuration behind each child score.
 */
export const PublicationConfigSnapshot = z
  .object({ ...configFields, children: z.array(PublicationChild).optional() })
  .meta({ id: "PublicationConfigSnapshot" });
export type PublicationConfigSnapshot = z.infer<typeof PublicationConfigSnapshot>;

export const ScorecardPublication = z
  .object({
    company_id: Uuid,
    id: Uuid,
    ...subject,
    period_type: PeriodType,
    period_key: PeriodKey,
    revision: z.int().min(1),
    snapshot: Scorecard,
    /**
     * The frozen client view of the same revision: client-visible metrics only, scores computed
     * from them (and, for a company, from the children's frozen client scores).
     */
    client_snapshot: ClientScorecard,
    config_snapshot: PublicationConfigSnapshot,
    reason: z.string().nullable(),
    published_at: Timestamp,
    published_by_membership_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ScorecardPublication" });
export type ScorecardPublication = z.infer<typeof ScorecardPublication>;

export const FridayReportRecord = z
  .object({
    company_id: Uuid,
    id: Uuid,
    week_key: WeekKey,
    version: z.int().min(1),
    state: FridayReportState,
    snapshot: FridayReport.nullable(),
    html_object_key: z.string().nullable(),
    pdf_object_key: z.string().nullable(),
    recipients: z.array(ReportRecipient),
    supervisor_note: z.string().nullable(),
    context_sources: z.array(ContextSource),
    generated_at: Timestamp.nullable(),
    /** First send started: content and recipients of this version are fixed (S1-14). */
    frozen_at: Timestamp.nullable(),
    sent_at: Timestamp.nullable(),
    send_error: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "FridayReportRecord" });
export type FridayReportRecord = z.infer<typeof FridayReportRecord>;

// Cost and time --------------------------------------------------------------------------------

export const CostEvent = z
  .object({
    company_id: Uuid,
    occurred_at: Timestamp,
    id: Uuid,
    period_key: MonthKey,
    kind: CostEventKind,
    amount_minor: MinorAmount,
    currency: Currency,
    amount_micros: MicrosAmount.nullable(),
    seat_id: Uuid.nullable(),
    holder_membership_id: Uuid.nullable(),
    meter_identity: z.string(),
    meter: CostMeter.nullable(),
    source_ref: SourceRef.nullable(),
    reverses_cost_event_id: Uuid.nullable(),
    price_list_ref: z.string().nullable(),
    visibility: Visibility,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "CostEvent" });
export type CostEvent = z.infer<typeof CostEvent>;

/** M3-04 (D-15): an operator's settlement of a stuck meter identity (`cost_meter_settlement`). */
export const MeterSettlement = z
  .object({
    company_id: Uuid,
    id: Uuid,
    meter_identity: z.string(),
    receipt_hash: z.string().length(64),
    resolution: MeterSettlementResolution,
    original_cost_event_id: Uuid,
    cost_event_id: Uuid,
    cost_event_occurred_at: Timestamp,
    reversal_cost_event_id: Uuid.nullable(),
    amount_minor: MinorAmount,
    amount_micros: MicrosAmount.nullable(),
    reason: z.string(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "MeterSettlement" });
export type MeterSettlement = z.infer<typeof MeterSettlement>;

export const DeSubscription = z
  .object({
    company_id: Uuid,
    id: Uuid,
    seat_id: Uuid,
    membership_id: Uuid,
    amount_minor: MinorAmount,
    currency: Currency,
    cadence: SubscriptionCadence,
    start_month: MonthKey,
    end_month: MonthKey.nullable(),
    scope: Visibility,
    /** BILLING-PLANS: set on a maintenance line billed through the company's subscription. */
    maintenance_plan_id: Uuid.nullable(),
    tier: MaintenanceTier.nullable(),
    started_on: IsoDate.nullable(),
    ended_on: IsoDate.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "DeSubscription" });
export type DeSubscription = z.infer<typeof DeSubscription>;

export const TimeEntry = z
  .object({
    company_id: Uuid,
    id: Uuid,
    seat_id: Uuid,
    membership_id: Uuid,
    kind: TimeEntryKind,
    grain: TimeEntryGrain,
    work_date: IsoDate,
    period_key: PeriodKey,
    started_at: Timestamp.nullable(),
    ended_at: Timestamp.nullable(),
    duration_seconds: z.int().positive(),
    minutes: z.int(),
    source: TimeEntrySource,
    source_event_id: z.string(),
    revision: z.int().min(1),
    supersedes_id: Uuid.nullable(),
    superseded_by: Uuid.nullable(),
    voided_at: Timestamp.nullable(),
    approval_status: ApprovalStatus,
    decided_by_membership_id: Uuid.nullable(),
    decided_at: Timestamp.nullable(),
    rejection_reason: z.string().nullable(),
    source_ref: z.record(z.string(), z.unknown()).nullable(),
    source_actor_ref: z.string().nullable(),
    dims: TimeEntryDims.nullable(),
    stats: TimeEntryStats.nullable(),
    visibility: Visibility,
    note: z.string().nullable(),
    /** WOS-BUNDLE: the task the time was spent on (timer, a task's manual entry). */
    task_id: Uuid.nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "TimeEntry" });
export type TimeEntry = z.infer<typeof TimeEntry>;

// Entitlements and operations ------------------------------------------------------------------

export const EntitlementCacheRow = z
  .object({
    company_id: Uuid,
    feature_key: AddonKey,
    status: EntitlementStatus,
    read_only_until: IsoDate.nullable(),
    synced_at: Timestamp,
  })
  .meta({ id: "EntitlementCacheRow" });
export type EntitlementCacheRow = z.infer<typeof EntitlementCacheRow>;

export const CompanyEntitlement = z
  .object({
    id: Uuid,
    company_id: Uuid,
    feature_key: AddonKey,
    status: EntitlementStatus,
    source: EntitlementSource,
    bundled_by_feature_key: AddonKey.nullable(),
    starts_on: IsoDate,
    ends_on: IsoDate.nullable(),
    grace_until: IsoDate.nullable(),
    granted_by_user_id: Uuid.nullable(),
    note: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "CompanyEntitlement" });
export type CompanyEntitlement = z.infer<typeof CompanyEntitlement>;

export const ConsumerProcessing = z
  .object({
    consumer_name: z.string(),
    company_id: Uuid,
    event_id: Uuid,
    event_seq: z.int().nullable(),
    state: ConsumerState,
    attempts: z.int().min(0).max(5),
    last_error: z.string().nullable(),
    processed_at: Timestamp,
    next_attempt_at: Timestamp.nullable(),
  })
  .meta({ id: "ConsumerProcessing" });
export type ConsumerProcessing = z.infer<typeof ConsumerProcessing>;
